import moment from 'moment'
import axios from 'axios'
import { EventBus } from '@/event-bus.js'

import apiSettings from '@/api/settings'
import Auth0AuthService from '@/auth/Auth0AuthService'

import * as sessionService from '@/auth/SessionAuthService'

import { BroadcastChannel } from 'broadcast-channel'

let auth
if (process.env.VUE_APP_AUTH_MODE === 'session') {
  // Autenticacion por Cookies de Session
  auth = sessionService
} else {
  // Por defecto se autentica con AUTH0
  auth = new Auth0AuthService()
}

// initial state
const state = {
  isAuthenticated: !!localStorage.getItem('access_token'),
  accessToken: localStorage.getItem('access_token'),
  idToken: localStorage.getItem('id_token'),
  msToRenewToken: localStorage.getItem('ms_to_renew_token'),
  tokenExpiryWarning: localStorage.getItem('token_expiry_warning'),
  modalTimeout: '',
  expiresAt: localStorage.getItem('expires_at'),
  avatar: localStorage.getItem('avatar') ? localStorage.getItem('avatar') : 'https://via.placeholder.com/125x125',
  profilePicture: localStorage.getItem('profilePicture'),
  userInitials: localStorage.getItem('userInitials'),
  role: localStorage.getItem('role') ? localStorage.getItem('role') : '',
  roles: localStorage.getItem('roles') !== null && localStorage.getItem('roles') !== 'undefined' ? JSON.parse(localStorage.getItem('roles')) : [],
  privileges: localStorage.getItem('privileges') !== null && localStorage.getItem('privileges') !== 'undefined' ? JSON.parse(localStorage.getItem('privileges')) : [],
  username: localStorage.getItem('username') ? localStorage.getItem('username') : '',
  nickname: localStorage.getItem('nickname') ? localStorage.getItem('nickname') : '',
  customerName: localStorage.getItem('customerName') ? localStorage.getItem('customerName') : '',
  tokenRenewalTimeout: false,
  routesConfigs: localStorage.getItem('routesConfigs') === null ? [] : JSON.parse(localStorage.getItem('routesConfigs')),
  bVersion: localStorage.getItem('bVersion') || '',
  id: null,
  extra: {},
  // Calculo el cancelToken a utilizar apenas carga la app
  // https://github.com/lumile/lumbre/issues/611
  cancelTokenSource: axios.CancelToken.source(),
  emailWasConfirmed: false,
  plan: {}
}

const getters = {
  isAuthenticated (state) {
    return state.isAuthenticated
  },
  accountUserName (state) {
    return state.username || state.nickname
  },
  userPrivileges (state) {
    return state.privileges
  },
  daysLeft (state) {
    return state.plan.daysLeft
  },
  isOnTrial (state) {
    return state.plan.isOnTrial
  },
  emailWasConfirmed (state) {
    return state.emailWasConfirmed
  },
  userRole (state) {
    return state.role
  },
  isSuperUser (state) {
    return state.role === 'Superusuario'
  },
  userInfo (state) {
    // Informacion basica del usuario logueado
    return {
      username: state?.username || null,
      nickname: state?.nickname || null,
      customerName: state?.customerName || null,
      id: state?.id || null,
      avatar: state?.profilePicture || state?.avatar || null,
      initials: state?.userInitials || null,
      firstname: state?.extra?.firstname || null,
      lastname: state?.extra?.lastname || null,
      email: state?.extra?.email || null,
      area: state?.extra?.area || null,
      subarea: state?.extra?.subarea || null,
      position: state?.extra?.position || null,
      signature: state?.extra?.signature || null,
      signaturetype: state?.extra?.signaturetype || null,
      role: state?.role || null
    }
  }
}

const actions = {
  async login ({ dispatch }, data) {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await auth.login(data)
        console.log('result', result)
        if (result === 'success') {
          await dispatch('getUserInfo')
        } else if (result?.reason === 'extraDataNedeed') {
          console.log('necesito mas data', result)
        }
        // EventBus.$emit('routePush', { path: '/' })
        resolve(result)
      } catch (error) {
        console.log('login error')
        reject(error)
      }
    })
  },
  async logout ({ commit, state, dispatch }) {
    return new Promise(async (resolve, reject) => {
      try {
        if (state.isAuthenticated) {
          state.cancelTokenSource.cancel()
          commit('settings/mutateResetDashSettings', false, { root: true })
          commit('mutateLogout')
          await auth.logout()
        }
        resolve(true)
      } catch (error) {
        reject(error)
      } finally {
        // location.href = '/login'
        const channel = new BroadcastChannel('logout')
        channel.postMessage(true)
      }
    })
  },
  handleAuthentication ({ commit, dispatch, state }) {
    return auth.handleAuthentication()
      .then(authResult => {
        commit('mutateAuthenticated', authResult)
        // Se dispara la acción para completar la configuracion del VueRouter
        // dispatch('getRoutesConfigs')
        const tokenRenewalTimeout = setTimeout(() => {
          dispatch('updateAuthTokens')
        }, state.msToRenewToken)
        commit('mutateTokenRenewalTimeout', tokenRenewalTimeout)
      })
      .catch(error => {
        if (process.env.NODE_ENV === 'development') {
          console.log(error)
        }
        dispatch('logout')
      })
  },
  async getUserInfo ({ commit }) {
    try {
      const user = await auth.getUserInfo()
      commit('mutateProfile', user)
      return user
    } catch (error) {
      console.log(error)
    }
  },
  updateAuthTokens ({ commit, dispatch, state }) {
    if (process.env.NODE_ENV === 'development') {
      console.log('El token expiro y se tiene que renovar')
    }

    return new Promise((resolve, reject) => {
      auth.updateTokens()
        .then(authResult => {
          commit('mutateAuthenticated', authResult)
          // Callback
          const tokenRenewalTimeout = setTimeout(() => {
            dispatch('updateAuthTokens')
          }, state.msToRenewToken)
          commit('mutateTokenRenewalTimeout', tokenRenewalTimeout)
          resolve()
        })
        .catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.log('store/session error:', error)
          }
          reject(error)
          dispatch('logout')
        })
    })
  },
  reScheduleUpdateAuthTokens ({ commit, dispatch, state }) {
    if (state.isAuthenticated) {
      if (process.env.NODE_ENV === 'development') {
        console.log('reScheduleUpdateAuthTokens')
      }
      const tokenRenewalTimeout = setTimeout(() => {
        dispatch('updateAuthTokens')
      }, state.msToRenewToken)
      commit('mutateTokenRenewalTimeout', tokenRenewalTimeout)
    }
  },
  /**
   * Dependencia del tk #164 - funcionalidad freezada reimplementada en router.js
   * se deja logica por unos commits para confirmar si se va a utilizar vuex para
   * persistir los datos del router o no.
   * @param {*} param0
   */
  getRoutesConfigs ({ commit }) {
    return apiSettings.getRoutesConfigs()
      .then(response => {
        commit('mutateRoutesConfig', response)
      })
      .catch(error => {
        console.log(error)
      })
  },
  /**
   * Es llamada por el job de crontab desde LumbreApp y valida con el expiresAt alojado en vuex
   * que se actualiza cada vez que pasa una petición por Axios
   * Si la sesión está a 3 minutos o menos de vencer se avisa al usuario brindando 2 minutos
   * de tiempo para aceptar continuar navegando o desloguearse.
   * Esto antes de que la sesión sea deslogueada por enviar una cookie vencida.
   * @param
   */
  async preventSessionToExpire ({ state }) {
    const timeBeforeNotify = 3 * 60 * 1000 // 3 minutos
    // Verifico si la sesión ya expiró debido al delay que existe cuando se navega en otra pestaña
    if (state.expiresAt < Date.now()) {
      EventBus.$emit('sessionExpired')
      const channel = new BroadcastChannel('sessionExpired')
      channel.postMessage(true)
    } else if (state.expiresAt - timeBeforeNotify < Date.now()) {
      // Se envía evento atajado por el modal session-expire en LumbreApp.vue
      EventBus.$emit('sessionAboutToExpire')
    }
  }
}

const mutations = {
  /**
   * Si la sesión está a 3 minutos o menos de vencer se avisa al usuario brindando 2 minutos
   * de tiempo para aceptar continuar navegando o desloguearse.
   * Esto antes de que la sesión sea deslogueada por enviar una cookie vencida.
   * @param
   */
  updateTokenExpiresAt (state, props) {
    if (process.env.VUE_APP_SOCKET_ENABLED === 'yes' && process?.env?.VUE_APP_LUMBRE_SESSION_MODAL === 'yes') {
      // El TTL (time to live) es enviado por el interceptor de Axios para saber cuanto tiempo falta para que expire la cookie
      // Este if es un fix ya que al hacer el upload de un archivo no se devuelve el ttl correctamente
      if (!isNaN(props.ttl)) {
        const timeBeforeNotify = 3 * 60 * 1000 // 3 minutos
        const timeToShowModal = props.ttl - timeBeforeNotify
        // Actualizo el state con el momento en el que expirará la sesión
        state.expiresAt = props.ttl + new Date().getTime()
        EventBus.$emit('sessionUpdated', { ttl: timeToShowModal })
        // Envio información de TTL por BC
        const channel = new BroadcastChannel('sessionUpdated')
        channel.postMessage(timeToShowModal)
      }
    }
  },
  mutateAuthenticated (state, authData) {
    state.isAuthenticated = true
    state.accessToken = authData.accessToken
    state.idToken = authData.idToken
    // The expires_in parameter indicates how many seconds the Access Token will be valid for, and can be used to anticipate expiration of the Access Token.
    // https://auth0.com/docs/api-auth/tutorials/silent-authentication#access-token-expiration
    // expiresAt is the representation in epoch time of when the token will expiry.
    state.expiresAt = authData.expiresIn * 1000 + new Date().getTime()

    // tokenExpiryWarning es un epoch time que representa la fecha en la cual se va a gestionar la renovacion del
    // token, esta fecha esta basada en state.expiresAt menos process.env.VUE_APP_JWT_TOKEN_RENEW_MINUTES_BEFORE_EXPIRY
    state.tokenExpiryWarning = moment(state.expiresAt).subtract(process.env.VUE_APP_JWT_TOKEN_RENEW_MINUTES_BEFORE_EXPIRY, 'minutes')

    state.msToRenewToken = state.tokenExpiryWarning.diff(moment())
    if (process.env.NODE_ENV === 'development') {
      console.log('expiresIn', authData.expiresIn)
      console.log('expiresAt', state.expiresAt)
      console.log('msToRenewToken', state.msToRenewToken)
    }
    localStorage.setItem('access_token', state.accessToken)
    localStorage.setItem('id_token', state.idToken)
    localStorage.setItem('expires_at', state.expiresAt)
    localStorage.setItem('ms_to_renew_token', state.msToRenewToken)
    localStorage.getItem('token_expiry_warning', state.tokenExpiryWarning)
  },
  mutateLogout (state) {
    // Dejo seteado un nuevo valor para el canceltoken luego del logout.
    // https://github.com/lumile/lumbre/issues/611
    state.cancelTokenSource = axios.CancelToken.source()
    clearTimeout(state.tokenRenewalTimeout)
    state.isAuthenticated = false
    state.accessToken = null
    state.idToken = false
    state.routesConfigs = []
    state.extra = {}
    state.id = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('id_token')
    localStorage.removeItem('expires_at')
    localStorage.removeItem('ms_to_renew_token')
    localStorage.removeItem('token_expiry_warning')
    localStorage.removeItem('avatar')
    localStorage.removeItem('profilePicture')
    localStorage.removeItem('userInitials')
    localStorage.removeItem('id')
    localStorage.removeItem('role')
    localStorage.removeItem('roles')
    localStorage.removeItem('privileges')
    localStorage.removeItem('username')
    localStorage.removeItem('nickname')
    localStorage.removeItem('customerName')
    localStorage.removeItem('routesConfigs')
    localStorage.removeItem('bVersion')
  },
  mutateProfile (state, userProfile) {
    state.emailWasConfirmed = userProfile?.emailWasConfirmed
    state.plan = userProfile?.plan

    if (userProfile.profilePicture) {
      state.profilePicture = userProfile.profilePicture
    } else {
      state.profilePicture = null
    }

    if (userProfile.userInitials) {
      state.userInitials = userProfile.userInitials
    } else {
      state.userInitials = ''
    }

    if (userProfile.extra?.nickname) {
      state.nickname = userProfile.extra.nickname
    } else {
      state.nickname = null
    }

    if (userProfile.extra?.customerName) {
      state.customerName = userProfile.extra.customerName
    } else {
      state.customerName = null
    }

    state.username = `${userProfile.extra?.firstname} ${userProfile.extra?.lastname}`

    if (userProfile.role) {
      state.role = userProfile.role
    } else {
      state.role = ''
    }
    if (userProfile.privileges) {
      state.privileges = userProfile.privileges
    } else {
      state.privileges = ''
    }
    if (userProfile.extra) {
      state.extra = userProfile.extra
    } else {
      state.extra = {}
    }

    if (userProfile.id) {
      state.id = userProfile.id
    } else {
      state.id = null
    }
    if (process.env?.VUE_APP_ROLLBAR_ENABLE === 'yes' && process.env.NODE_ENV !== 'development' && this._vm?.$rollbar) {
      this._vm.$rollbar.configure({
        payload: {
          person: {
            id: state.id
          }
        }
      })
    }
    state.roles = userProfile.roles
    if (process.env?.VUE_APP_ENABLE_REAMAZE === 'yes') {
      window._support['user'] = {
        id: state.id,
        email: state.extra.email,
        name: state.username,
        data: {
          customerName: userProfile?.extra?.customerName || 'Desconocido',
          backendVersion: userProfile?.extra?.bVersion
        }
      }
      if (userProfile?.extra?.rmzAK) {
        window._support['user'].authkey = userProfile.extra.rmzAK
      }
      window.Reamaze.reload()
    }
    state.bVersion = userProfile.extra.bVersion

    localStorage.setItem('avatar', state.avatar)
    localStorage.setItem('profilePicture', state.profilePicture)
    localStorage.setItem('userInitials', state.userInitials)
    localStorage.setItem('id', state.id)
    localStorage.setItem('extra', state.extra)
    localStorage.setItem('username', state.username)
    localStorage.setItem('nickname', state.nickname)
    localStorage.setItem('customerName', state.customerName)
    localStorage.setItem('role', state.role)
    localStorage.setItem('roles', JSON.stringify(state.roles))
    localStorage.setItem('privileges', JSON.stringify(state.privileges))
    localStorage.setItem('bVersion', state.bVersion)
  },
  mutateProfilePicture (state, profilePicture) {
    state.profilePicture = profilePicture
    localStorage.setItem('profilePicture', state.profilePicture)
  },
  mutateName (state, { firstname, lastname, userInitials }) {
    state.userInitials = userInitials
    localStorage.setItem('userInitials', state.userInitials)
    state.username = `${firstname} ${lastname}`
    localStorage.setItem('username', state.username)
  },
  mutateTokenRenewalTimeout (state, newTokenRenewalTimeout) {
    const oldTokenRenewalTimeout = state.tokenRenewalTimeout
    clearTimeout(oldTokenRenewalTimeout)
    if (process.env.NODE_ENV === 'development') {
      console.log('mutateTokenRenewalTimeout', newTokenRenewalTimeout)
      console.log('msToRenewToken', state.msToRenewToken)
    }
    state.tokenRenewalTimeout = newTokenRenewalTimeout
  },
  mutateSignature (state, signature) {
    const copy = { ...state }
    copy.extra.signature = signature
    state = copy
  },
  mutateExpiresAt (state, ttl) {
    state.expiresAt = ttl + new Date().getTime()
  },
  /**
   * Dependencia del tk #164 - funcionalidad freezada reimplementada en router.js
   * se deja logica por unos commits para confirmar si se va a utilizar vuex para
   * persistir los datos del router o no.
   * @param {} state
   * @param {*} apiResponse
   */
  mutateRoutesConfig (state, response) {
    state.routesConfigs = response.routesConfigs
    localStorage.setItem('routesConfigs', JSON.stringify(state.routesConfigs))
  },
  mutateCustomerId (state, id) {
    state.extra.customerId = id
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
